import Promise from 'bluebird';
import React from 'react';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import { extend, isEmpty } from 'underscore';
import { endsWith } from 'underscore.string';
import { AdCustomizerBaseAnalyzer } from '../base-analyzer';
import { evaluatelocationRules, parseRule } from './parser';
import LocationEditor from './editor';
import { displayLength } from '../string-utils';

const pickerLocationInsert = '{LOCATION(CITY)}';
const pickerLocationInsertCursorPosition = pickerLocationInsert.length - 1;
export class AdCustomizerLocationAnalyzer extends AdCustomizerBaseAnalyzer {
  constructor({ deps: { i18n, instrumentation }, controller }) {
    super({
      name: 'location',
      priority: 3000,
      i18n,
      activityName: 'location',
      instrumentation,
      controller,
    });
    this.controller = controller;
    this.defaultAnalyzeResult = parseRule(pickerLocationInsert, pickerLocationInsertCursorPosition);
  }

  /* return a reactjs object here */
  getSyntax(analyzeResult) {
    if (!analyzeResult.options) return null;
    const locationText = `LOCATION(${this.i18n.getString(_TL_('Level'))})`;
    const syntax = `${this.i18n.getString(_TL_('Syntax:'))} {${locationText}:${this.i18n.getString(_TL_('Default text'))}}`;
    return (
      <div>
        <span
          className="locationEditor-syntax"
        >{syntax}
        </span>
        <HelpPopupInline
          topic="pop_BA_NewAdCreate_LocationInsert_Syntax"
          ariaLabel={this.i18n.getString(_TL_('Click to learn more about {{item}}', { item: this.i18n.getString(_TL_('Syntax')) }))}
        />
      </div>);
  }

  // get menu item
  getMenuItem({
    startIndex, endIndex, updateAndCloseCallback, target,
  }) {
    return {
      name: this.i18n.getString(_TL_('Location insertion')),
      description: this.i18n.getString(_TL_('Insert the location that triggers your ad')),
      onClick: () => {
        const beginning = target.value.substring(0, startIndex);
        const end = target.value.substring(endIndex);
        const newValue = `${beginning}${pickerLocationInsert}${end}`;
        const suffix = target.value.substring(endIndex);
        this.defaultAnalyzeResult = parseRule(newValue, newValue.length - suffix.length - 1);

        updateAndCloseCallback({
          newValue,
          cursorPosition: beginning.length + pickerLocationInsertCursorPosition,
          isSelectedByUser: true,
        });

        this.controller.updatePicker({
          target,
          syntax: this.getSyntax(this.defaultAnalyzeResult),
          editorView: this.getEditor({
            target,
            analyzeResult: this.defaultAnalyzeResult,
            updateAndCloseCallback,
          }),
          container: this.controller.container,
        });
      },
    };
  }

  needEditor(analyzeResult) {
    const { options } = analyzeResult || {};
    return !isEmpty(options);
  }

  getEditor({ analyzeResult, updateAndCloseCallback }) {
    const { options } = analyzeResult || {};

    return (<LocationEditor
      i18n={this.i18n}
      options={options}
      updateAndCloseCallback={updateAndCloseCallback}
    />);
  }

  analyzeInput({
    text, cursorIndex, updateAndCloseCallback, target,
  }) {
    const rule = parseRule(text, cursorIndex);

    if (rule) {
      const { options: { startIndex } } = rule;
      return Promise.resolve(extend(
        {
          match: true,
          type: this.name,
          defaultHandlerForInput: false,
          itemsToDisplay: [this.getMenuItem({
            startIndex, endIndex: cursorIndex, updateAndCloseCallback, target,
          })],
        },
        rule
      ));
    }
    // when not match, see whether to show menu item
    if (text && endsWith(text, '{', cursorIndex)) {
      return Promise.resolve({
        type: this.name,
        defaultHandlerForInput: false,
        itemsToDisplay: [this.getMenuItem({
          startIndex: text.length - 1,
          endIndex: cursorIndex,
          updateAndCloseCallback,
          target,
        })],
      });
    }
    return Promise.resolve(null);
  }

  evaluateRule(text) {
    return evaluatelocationRules(text);
  }

  getCharCountExcludingAdCustomizers(text) {
    let charCount = 0;
    let processedText = text || '';

    let tokens = /\{location\((city|state|country)\)}/i.exec(processedText);
    if (tokens) {
      processedText = processedText.replace(tokens[0], '');
      charCount = 10;
    } else {
      tokens = /\{location\((city|state|country)\):([^{}]*)}/i.exec(processedText);
      if (tokens) {
        processedText = processedText.replace(tokens[0], '');
        charCount += tokens[2] ? displayLength(tokens[2]) : 0;
      }
    }

    return {
      processedText,
      charCount,
    };
  }
}
